<template>
  <TransitionRoot appear :show="true" as="template">
    <Dialog as="div" class="relative z-10">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="
                w-full
                max-w-xl
                transform
                overflow-hidden
                rounded-xl
                bg-white
                text-left
                align-middle
                shadow-xl
                transition-all
                p-4
              "
            >
              <DialogOverlay class="p-5 pt-5">
                <div class="col-span-2 block mb-4">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300" />
                    </div>

                    <div class="relative flex justify-between">
                      <h2 class="font-bold pr-3 bg-white col-md-6 text-xl text-vice-teal-900">
                        Edit student
                      </h2>
                      <span class="p-1 pl-3 bg-white cursor-pointer" @click="closeModal">
                        <CrossGrayIcon class="h-6 w-6" />
                      </span>
                    </div>
                  </div>
                </div>

                <div class="container max-w-full justify-between text-sm">
                  <form @submit.prevent="submit" class="p-2 relative">
                    <div>
                      <jet-label for="name" value="Name *" />
                      <jet-input
                        data-cy="edit-name"
                        id="name"
                        name="name"
                        type="text"
                        class="mt-1 block w-2/3"
                        v-model="form.name"
                        required
                        autocomplete="name"
                      />
                      <div v-if="this.$page.props.errors">
                        <ul class="mt-3 list-disc list-inside text-sm text-red-600">
                          <li v-for="(error, key) in this.$page.props.errors.name" :key="key">
                            {{ error }}
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="mt-4">
                      <jet-label for="email" value="Email address *" />
                      <jet-input
                        data-cy="edit-email"
                        id="email"
                        name="email"
                        type="email"
                        class="mt-1 block w-2/3"
                        v-model="form.email"
                        required
                        autocomplete="on"
                      />
                      <div v-if="this.$page.props.errors">
                        <ul class="mt-3 list-disc list-inside text-sm text-red-600">
                          <li v-for="(error, key) in this.$page.props.errors.email" :key="key">
                            {{ error }}
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div class="mt-4">
                      <jet-label for="group_id" value="Select group" />
                      <select
                        data-cy="user-register-group"
                        id="group_id"
                        name="group_id"
                        class="
                          mt-1
                          block
                          w-full
                          border-gray-300
                          focus:border-indigo-300
                          focus:ring focus:ring-indigo-200 focus:ring-opacity-50
                          rounded-md
                          shadow-sm
                          sm:text-sm
                        "
                        v-model="form.group_id"
                      >
                        <option :value="null" :selected="this.user.group_id === null">-</option>
                        <option
                          v-for="group in groups"
                          :key="group.id"
                          :value="group.id"
                          :selected="group.id === this.user.group_id"
                        >
                          {{ group.name }}
                        </option>
                      </select>
                    </div>
                    <div class="flex mt-8 gap-3">
                      <jet-button
                        data-cy="edit-user"
                        class=""
                        :class="{ 'opacity-25': form.processing }"
                        :disabled="form.processing || !isFormChanged"
                      >
                        Update
                      </jet-button>
                      <button
                        data-cy="save-as-draft"
                        type="button"
                        @click="closeModal()"
                        class="
                          inline-flex
                          items-center
                          px-4
                          py-2
                          border border-gray-300
                          rounded-md
                          shadow-sm
                          text-sm
                          font-medium
                          text-gray-700
                          bg-white
                          hover:bg-gray-50
                          focus:outline-none
                          focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                        "
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>
              </DialogOverlay>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetButton from '@/Jetstream/Button.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';

export default {
  name: 'EditUserModal',
  props: {
    user: {
      type: Object,
    },
  },
  emits: ['toggleEditUserModal'],
  components: {
    CrossGrayIcon,
    JetButton,
    JetInput,
    JetLabel,
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogOverlay,
  },

  data() {
    return {
      form: this.$inertia.form({
        name: this.user.name,
        email: this.user.email,
        group_id: this.user.group_id,
      }),
      originalForm: {
        name: this.user.name,
        email: this.user.email,
        group_id: this.user.group_id,
      },
      groups: [],
    };
  },

  mounted() {
    this.loadGroups(this.user.university_id);
  },
  computed: {
    isFormChanged() {
      return (
        this.form.name !== this.originalForm.name ||
        this.form.email !== this.originalForm.email ||
        this.form.group_id !== this.originalForm.group_id
      );
    },
  },

  methods: {
    async loadGroups(universityId) {
      try {
        const response = await axios.get(`/api/universities/${universityId}/groups`);
        this.groups = response.data;
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    },

    async submit() {
      const data = this.form.data();
      await axios
        .post(this.route('admin.update.user', this.user.id), data)
        .then(() => {
          const selectedGroup = this.groups.find((group) => group.id === data.group_id);
          this.closeModal({ ...data, id: this.user.id, group_name: selectedGroup?.name ?? null });
        })
        .catch((error) => {
          console.error('Error loading groups:', error);
          this.$page.props.errors = error.response?.data?.errors;
        });
    },

    closeModal(user = null) {
      this.$emit('toggleEditUserModal', false, user);
    },
  },

  unmounted() {
    this.$page.props.errors = [];
  },
};
</script>

<style scoped>
button:disabled {
  opacity: 0.7;
}
</style>
